import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CG_DISTRICT_LIST } from "@/lib/cgLocations";
import { MapPin, Users, FileText, Clock, Send } from "lucide-react";

export default function ReporterMapPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reporter-map").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="p-10 text-center text-slate-500">लोड हो रहा है…</div>;

  // Build a map of district -> data for O(1) lookup
  const districtMap = {};
  data.districts.forEach((d) => { districtMap[d.district] = d; });
  const maxTotal = Math.max(1, ...data.districts.map((d) => d.total));

  const bubbleSize = (t) => 32 + Math.round((t / maxTotal) * 40); // 32-72px

  const coverageColor = (t) => {
    if (t === 0) return { bg: "bg-slate-100", text: "text-slate-400", ring: "" };
    if (t <= 2) return { bg: "bg-amber-100", text: "text-amber-700", ring: "ring-amber-300" };
    if (t <= 5) return { bg: "bg-emerald-100", text: "text-emerald-700", ring: "ring-emerald-300" };
    return { bg: "bg-blue-100", text: "text-blue-700", ring: "ring-blue-400" };
  };

  return (
    <div data-testid="reporter-map-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><MapPin className="w-5 h-5 text-emerald-600"/>रिपोर्टर कवरेज मानचित्र</h1>
          <p className="text-sm text-slate-500 mt-1">छत्तीसगढ़ के हर ज़िले में कहाँ-कहाँ से रिपोर्ट आ रही हैं — ज़मीनी कवरेज एक नज़र में</p>
        </div>
        <Link to="/admin/reporter-approvals" className="inline-flex items-center gap-1.5 text-sm bg-[#0F172A] hover:bg-[#1E3A8A] text-white px-4 py-2 rounded-md" data-testid="goto-approvals">
          <Send className="w-4 h-4"/>समीक्षा पैनल
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="कुल सबमिशन" value={data.totals.total_submissions} Icon={FileText} color="text-slate-700"/>
        <StatCard label="समीक्षाधीन" value={data.totals.pending} Icon={Clock} color="text-amber-600"/>
        <StatCard label="प्रकाशित" value={data.totals.approved} Icon={FileText} color="text-emerald-600"/>
        <StatCard label="कवर ज़िले" value={`${data.totals.districts_covered}/33`} Icon={MapPin} color="text-blue-600"/>
        <StatCard label="सक्रिय रिपोर्टर" value={data.totals.active_reporters} Icon={Users} color="text-rose-600"/>
      </div>

      {/* Grid map — 33 districts */}
      <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-200 p-5 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-6 bg-emerald-500"></div>
          <div className="font-devnagri-heading font-black text-[#0F172A]">छत्तीसगढ़ ज़िला कवरेज</div>
          <div className="ml-auto flex items-center gap-2 text-[10px] text-slate-600">
            <LegendPill className="bg-slate-100 text-slate-500">कोई नहीं</LegendPill>
            <LegendPill className="bg-amber-100 text-amber-700">1-2</LegendPill>
            <LegendPill className="bg-emerald-100 text-emerald-700">3-5</LegendPill>
            <LegendPill className="bg-blue-100 text-blue-700">6+</LegendPill>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-3">
          {CG_DISTRICT_LIST.map((name) => {
            const d = districtMap[name] || { total: 0, pending: 0, approved: 0 };
            const c = coverageColor(d.total);
            const size = bubbleSize(d.total);
            return (
              <div key={name} className={`relative aspect-square rounded-xl border border-slate-200 ${c.bg} p-2 flex flex-col items-center justify-center text-center transition hover:shadow-md ${d.total > 0 ? "ring-2 " + c.ring : ""}`}
                title={`${name}: ${d.total} कुल · ${d.pending} pending`}
                data-testid={`map-district-${name}`}>
                <div className="rounded-full bg-white/60 flex items-center justify-center font-black font-devnagri-heading transition"
                  style={{ width: `${size}px`, height: `${size}px`, fontSize: `${Math.max(14, size * 0.35)}px` }}>
                  <span className={c.text}>{d.total}</span>
                </div>
                <div className="text-[10px] font-bold text-slate-700 leading-tight mt-1 truncate w-full">{name}</div>
                {d.pending > 0 && <span className="absolute top-1 right-1 bg-red-500 text-white text-[9px] font-black rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{d.pending}</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* District breakdown */}
      {data.districts.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 font-bold text-[#0F172A]">ज़िले-वार ब्रेकडाउन</div>
          <div className="divide-y divide-slate-100">
            {data.districts.map((d) => (
              <details key={d.district} className="group" data-testid={`district-row-${d.district}`}>
                <summary className="px-5 py-3 cursor-pointer flex items-center gap-3 hover:bg-slate-50">
                  <MapPin className="w-4 h-4 text-emerald-600"/>
                  <div className="font-bold text-[#0F172A]">{d.district}</div>
                  <div className="ml-auto flex items-center gap-3 text-xs">
                    <span className="text-slate-500">कुल: <b className="text-slate-800">{d.total}</b></span>
                    {d.pending > 0 && <span className="text-amber-700">समीक्षाधीन: <b>{d.pending}</b></span>}
                    <span className="text-emerald-700">प्रकाशित: <b>{d.approved}</b></span>
                  </div>
                </summary>
                <div className="px-5 pb-4 pt-1 flex flex-wrap gap-2">
                  {d.blocks.map((b) => (
                    <span key={b.block} className="text-[11px] bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-2.5 py-1">
                      {b.block} <b className="text-[#0F172A]">({b.total})</b>
                      {b.pending > 0 && <span className="ml-1 text-red-600">• {b.pending}</span>}
                    </span>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </div>
      )}

      {/* Active reporters */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 font-bold text-[#0F172A] flex items-center gap-2">
          <Users className="w-4 h-4 text-rose-600"/>सक्रिय फ़ील्ड रिपोर्टर ({data.reporters.length})
        </div>
        {data.reporters.length === 0 ? (
          <div className="p-10 text-center text-slate-500 italic">कोई सक्रिय रिपोर्टर नहीं — <Link to="/admin/staff" className="underline text-blue-600">स्टाफ में PIN सेट करें</Link></div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.reporters.map((r) => (
              <div key={r.staff_id} className="px-5 py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center font-black">
                  {(r.name || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#0F172A]">{r.name}</div>
                  <div className="text-[11px] text-slate-500">{r.designation} · <span className="font-mono">{r.staff_id}</span></div>
                </div>
                {r.phone && <a href={`tel:${r.phone}`} className="text-xs text-blue-600 hover:underline">{r.phone}</a>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, Icon, color }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-1"><Icon className={`w-4 h-4 ${color}`}/><span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</span></div>
      <div className={`text-2xl font-black ${color}`}>{value}</div>
    </div>
  );
}

function LegendPill({ children, className }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-bold ${className}`}>{children}</span>;
}
